import Client from "fhir-kit-client";
import i18n from "i18next";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Badge, Button, Card, Form, Pagination, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import VulcanPage from "../../components/VulcanPage/VulcanPage";
import {
  DeviceKind,
  FhirDevice,
  getAvailability,
  getCategoryDisplay,
  getDeviceCategoriesForFilter,
  getDeviceName,
  getDeviceSearchText,
  getDeviceVersion,
  getTypeDisplay,
  hasCategory,
  isDeviceKind,
} from "../../services/DeviceAdapter";
import "./DevicePages.css";

type Props = { kind: DeviceKind };

const PAGE_SIZE = 10;

const DeviceList: FunctionComponent<Props> = ({ kind }) => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<FhirDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const fhirClient = useMemo(
    () => new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" }),
    [],
  );

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const bundle: any = await fhirClient.search({
        resourceType: "Device",
        searchParams: { _sort: "-_lastUpdated" },
      });
      setDevices(
        (bundle.entry ?? [])
          .map((entry: any) => entry.resource)
          .filter((resource: any) => resource?.resourceType === "Device"),
      );
    } catch (error) {
      navigate("/Error", { state: { error } });
    } finally {
      setLoading(false);
    }
  }, [fhirClient, navigate]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const kindDevices = useMemo(
    () => devices.filter((device) => isDeviceKind(device, kind)),
    [devices, kind],
  );
  const categories = useMemo(
    () => getDeviceCategoriesForFilter(kindDevices),
    [kindDevices],
  );
  const statuses = useMemo(
    () =>
      Array.from(
        new Set(kindDevices.map((device) => device.status).filter(Boolean)),
      ).sort(),
    [kindDevices],
  );
  const visibleDevices = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return kindDevices.filter(
      (device) =>
        (!normalizedQuery ||
          getDeviceSearchText(device).includes(normalizedQuery)) &&
        (!category || hasCategory(device, category)) &&
        (!status || device.status === status),
    );
  }, [category, kindDevices, query, status]);
  const pageCount = Math.max(1, Math.ceil(visibleDevices.length / PAGE_SIZE));
  const paginatedDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return visibleDevices.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, visibleDevices]);
  const pageNumbers = useMemo(() => {
    const firstPage = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const lastPage = Math.min(pageCount, firstPage + 4);
    return Array.from(
      { length: lastPage - firstPage + 1 },
      (_, index) => firstPage + index,
    );
  }, [currentPage, pageCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [category, kind, query, status]);

  const labels =
    kind === "software"
      ? {
          title: "title.software",
          detailsRoute: "/SoftwareViewer/",
          register: true,
          registrationRoute: "/SoftwareRegistration",
        }
      : {
          title: "title.materials",
          detailsRoute: "/MaterialViewer/",
          register: true,
          registrationRoute: "/MaterialRegistration",
        };

  return (
    <VulcanPage titleKey={labels.title} loading={loading} needsLogin={true}>
      <div className="device-page section">
        {labels.register && (
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="danger"
              onClick={() => navigate(labels.registrationRoute)}
            >
              {i18n.t(
                kind === "software"
                  ? "button.registersoftware"
                  : "button.registermaterial",
              )}
            </Button>
          </div>
        )}
        <Card className="card mb-4">
          <Card.Header>
            <h2 className="h4 mb-0">{i18n.t("title.searchcriteria")}</h2>
          </Card.Header>
          <Card.Body>
            <div className="device-filters">
              <Form.Group>
                <Form.Label>{i18n.t("label.search")}</Form.Label>
                <Form.Control
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={i18n.t("label.searchPlaceholder")}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{i18n.t("label.category")}</Form.Label>
                <Form.Select
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  <option value="">{i18n.t("label.allCategories")}</option>
                  {categories.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>{i18n.t("label.status")}</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <option value="">{i18n.t("label.allStatuses")}</option>
                  {statuses.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setQuery("");
                  setCategory("");
                  setStatus("");
                }}
              >
                {i18n.t("button.reset")}
              </Button>
            </div>
          </Card.Body>
        </Card>

        <p className="device-result-count">
          {i18n.t("devices.resultCount", { count: visibleDevices.length })}
        </p>
        <div className="table-responsive">
          <Table hover className="align-middle device-table">
            <thead>
              <tr>
                <th>{i18n.t("label.name")}</th>
                <th>{i18n.t("label.category")}</th>
                <th>{i18n.t("label.type")}</th>
                <th>{i18n.t("label.status")}</th>
                <th>{i18n.t("label.availability")}</th>
                <th>{i18n.t("label.version")}</th>
                <th aria-label={i18n.t("label.actions")} />
              </tr>
            </thead>
            <tbody>
              {paginatedDevices.map((device) => (
                <tr key={device.id}>
                  <td>
                    <strong>{getDeviceName(device)}</strong>
                    <small className="d-block text-muted">
                      {device.manufacturer ?? ""}
                    </small>
                  </td>
                  <td>{getCategoryDisplay(device) || "—"}</td>
                  <td>{getTypeDisplay(device) || "—"}</td>
                  <td>
                    <Badge
                      bg={device.status === "active" ? "success" : "secondary"}
                    >
                      {device.status ?? "—"}
                    </Badge>
                  </td>
                  <td>{getAvailability(device) || "—"}</td>
                  <td>{getDeviceVersion(device) || "—"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() =>
                        navigate(`${labels.detailsRoute}${device.id}`)
                      }
                    >
                      {i18n.t("button.viewDetails")}
                    </Button>
                  </td>
                </tr>
              ))}
              {visibleDevices.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    {i18n.t("devices.noResults")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {visibleDevices.length > PAGE_SIZE && (
          <Pagination
            className="device-pagination"
            aria-label={i18n.t("devices.pagination.label")}
          >
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              {i18n.t("devices.pagination.previous")}
            </Pagination.Prev>
            {pageNumbers.map((page) => (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                aria-label={i18n.t("devices.pagination.page", { page })}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Pagination.Item>
            ))}
            <Pagination.Next
              disabled={currentPage === pageCount}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              {i18n.t("devices.pagination.next")}
            </Pagination.Next>
          </Pagination>
        )}
      </div>
    </VulcanPage>
  );
};

export default DeviceList;
